import React, { useState, useEffect } from 'react';
import { adminApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { User, Search, Ban, CheckCircle, Loader2, Mail, Phone } from 'lucide-react';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [processing, setProcessing] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, [roleFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await adminApi.getUsers({ role: roleFilter !== 'all' ? roleFilter : undefined });
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSuspend = async (userId, suspend) => {
    setProcessing(userId);
    try {
      await adminApi.suspendUser(userId, suspend);
      toast.success(`User ${suspend ? 'suspended' : 'unsuspended'}`);
      fetchUsers();
    } catch (error) {
      toast.error('Failed to update user');
    } finally {
      setProcessing(null);
    }
  };

  const filteredUsers = users.filter((user) =>
    user.name?.toLowerCase().includes(search.toLowerCase()) ||
    user.email?.toLowerCase().includes(search.toLowerCase())
  );

  const getRoleBadge = (role) => {
    const colors = {
      admin: 'bg-farmhouse-clay/20 text-farmhouse-clay',
      host: 'bg-farmhouse-saddle/20 text-farmhouse-saddle',
      guest: 'bg-farmhouse-moss/20 text-green-400',
    };
    return colors[role] || 'bg-farmhouse-stone/20 text-farmhouse-muted';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-farmhouse-charcoal flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-farmhouse-saddle animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-farmhouse-charcoal pt-8 pb-16" data-testid="admin-users-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 className="font-serif text-4xl font-semibold text-farmhouse-cream mb-8">
          Manage Users
        </h1>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-farmhouse-muted" />
            <Input
              type="text"
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="form-input pl-10"
              data-testid="search-users"
            />
          </div>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-full md:w-48 form-input" data-testid="filter-role">
              <SelectValue placeholder="Filter by role" />
            </SelectTrigger>
            <SelectContent className="bg-farmhouse-paper border-farmhouse-subtle">
              <SelectItem value="all" className="text-farmhouse-cream hover:bg-farmhouse-subtle">All Roles</SelectItem>
              <SelectItem value="guest" className="text-farmhouse-cream hover:bg-farmhouse-subtle">Guests</SelectItem>
              <SelectItem value="host" className="text-farmhouse-cream hover:bg-farmhouse-subtle">Hosts</SelectItem>
              <SelectItem value="admin" className="text-farmhouse-cream hover:bg-farmhouse-subtle">Admins</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Users Table */}
        <div className="bg-farmhouse-paper border border-farmhouse-subtle rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead>
                <tr className="bg-farmhouse-charcoal">
                  <th>User</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr key={user.id} data-testid={`user-row-${user.id}`}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="avatar bg-farmhouse-saddle text-white text-sm">
                          {user.name?.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-farmhouse-cream">{user.name}</p>
                          <div className="flex items-center gap-2 text-sm text-farmhouse-muted">
                            <Mail className="w-3 h-3" />
                            {user.email}
                          </div>
                          {user.phone && (
                            <div className="flex items-center gap-2 text-sm text-farmhouse-muted">
                              <Phone className="w-3 h-3" />
                              {user.phone}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <Badge className={`${getRoleBadge(user.role)} border-0 capitalize`}>
                        {user.role}
                      </Badge>
                    </td>
                    <td>
                      <div className="flex items-center gap-2">
                        <span className={`status-dot ${user.is_active ? 'accepted' : 'declined'}`} />
                        <span className="text-farmhouse-cream">
                          {user.is_active ? 'Active' : 'Suspended'}
                        </span>
                      </div>
                    </td>
                    <td className="text-farmhouse-muted">
                      {formatDate(user.created_at)}
                    </td>
                    <td>
                      {user.role !== 'admin' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSuspend(user.id, user.is_active)}
                          disabled={processing === user.id}
                          className={user.is_active 
                            ? 'border-farmhouse-clay text-farmhouse-clay hover:bg-farmhouse-clay/10'
                            : 'border-farmhouse-moss text-green-400 hover:bg-farmhouse-moss/10'
                          }
                          data-testid={user.is_active ? 'suspend-user' : 'unsuspend-user'}
                        >
                          {processing === user.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : user.is_active ? (
                            <>
                              <Ban className="w-4 h-4 mr-1" />
                              Suspend
                            </>
                          ) : (
                            <>
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Activate
                            </>
                          )}
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-12">
              <User className="w-12 h-12 mx-auto mb-4 text-farmhouse-subtle" />
              <p className="text-farmhouse-muted">No users found</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
